import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useCallback, useState } from "react";

import { MultiSelectOption } from "@/components/shared/multi-select/multi-select.view";

export type AddressNameLength = "long_name" | "short_name";

export function extractAddressComponent(
  components: google.maps.GeocoderAddressComponent[],
  types: string[],
  length: AddressNameLength,
): string | null {
  for (const type of types) {
    const name = components.find((c) => c.types.includes(type))?.[length];
    if (name) return name;
  }

  return null;
}

export type PlacesCoverage = "us" | "worldwide";

export const JOB_SEARCH_COVERAGE: PlacesCoverage = "us";

const ADDRESS_LANGUAGE = "en";

const COVERAGE_FILTERS: Record<
  PlacesCoverage,
  { region?: string; includedRegionCodes?: string[] }
> = {
  us: { region: "us", includedRegionCodes: ["US"] },
  worldwide: {},
};

export default function useGooglePlacesSuggestions({
  useStrictAddress,
  coverage,
}: {
  useStrictAddress: boolean;
  coverage: PlacesCoverage;
}) {
  const placesLib = useMapsLibrary("places");
  const geocodingLib = useMapsLibrary("geocoding");
  const [predictions, setPredictions] = useState<MultiSelectOption<string>[]>(
    [],
  );

  const searchPredictions = useCallback(
    async (input: string) => {
      if (!placesLib) {
        setPredictions([]);
        return;
      }

      const trimmedInput = input.trim();

      // Clear predictions if input is empty or just whitespace
      if (!trimmedInput) {
        setPredictions([]);
        return;
      }

      const results =
        await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: trimmedInput,
          language: ADDRESS_LANGUAGE,
          ...COVERAGE_FILTERS[coverage],
          includedPrimaryTypes: useStrictAddress
            ? ["street_address", "route", "premise", "subpremise"]
            : ["geocode"],
        });

      if (!results || !results.suggestions) {
        setPredictions([]);
        return;
      }

      const formattedPredictions = results.suggestions
        .map((suggestion) => {
          if (!suggestion.placePrediction) {
            return;
          }

          const placeId = (suggestion.placePrediction as { placeId?: string })
            .placeId;

          return {
            value: suggestion.placePrediction.text.text,
            label: suggestion.placePrediction.text.text,
            ...(placeId && { placeId }),
          };
        })
        .filter((item) => !!item);

      setPredictions(formattedPredictions);
    },
    [placesLib, useStrictAddress, coverage],
  );

  const fetchAddressDetails = useCallback(
    async (address: string): Promise<google.maps.GeocoderResult | null> => {
      if (!geocodingLib) throw new Error("Geocoding library not found");

      const geocoder = new geocodingLib.Geocoder();

      const { results } = await geocoder.geocode({
        address,
        region: COVERAGE_FILTERS[coverage].region,
        language: ADDRESS_LANGUAGE,
      });

      return results?.[0] || null;
    },
    [geocodingLib, coverage],
  );

  return {
    predictions,
    searchPredictions,
    fetchAddressDetails,
  };
}
